import _ from "lodash";
import React from "react";
import moment from "moment";
import {
  getSelectedAcademicYearId as getAcademicYearId,
  getSelectedAcademicYear as getAcademicYearObject,
} from "modules/CommonSelectors";
import { getTemplateUrl } from "SchoolSetup/modules/GraphqlHelpers";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import ACLStore from "lib/aclStore";
import {
  ENABLE_USER_ACADEMIC_YEAR_ARCHIVAL_STATUS,
  ENABLE_FILTERED_DATA_DOWNLOAD,
  CAN_CONFIGURE_SCHOOL_OVERVIEW,
  MANAGE_SCHOOL_ROSTER_PERMISSION,
  SCHOOL_ROSTER_VIEW_FAMILY_INVITE_AND_STUDENT_CODE_PERMISSION,
  SCHOOL_ROSTER_RESET_FAMILY_INVITE_AND_STUDENT_CODE_PERMISSION,
  MANAGE_SCHOOL_ROSTER_PERMISSION_FOR_ACCOUNT,
  SCHOOL_ROSTER_VIEW_FAMILY_INVITE_AND_STUDENT_CODE_PERMISSION_FOR_ACCOUNT,
  SCHOOL_ROSTER_RESET_FAMILY_INVITE_AND_STUDENT_CODE_PERMISSION_FOR_ACCOUNT,
  CAN_ADD_SUPER_ADMINS_FOR_ACCOUNT,
  CAN_IMPERSONATE_STAFF_PERMISSION,
  CAN_IMPERSONATE_STUDENT_PERMISSION,
  CAN_LOGOUT_STAFF_PERMISSION,
  CAN_CONFIGURE_LOGIN_OPTIONS,
  CAN_IMPERSONATE_STUDENT_CLASS_SETTINGS_PERMISSION,
  MANAGE_CLASS_ROSTER_PERMISSION,
} from "Constants/permissionConstants";
import { getAdminPortalNodeVisibilityStatusByUID } from "Administrator/modules/Utils";

import ACLStorage from "lib/aclService";
import {
  getIsAccountConfigurationEnabled,
  getIsNewAdminUIEnabled,
  getIsAcademicYearTransitionV2Enabled,
} from "Utils/ACLStoreUtils";
import { getCurriculumProgramLabel } from "Platform/modules/PlatformUtils";

export const EMPTY_ARRAY = [];
export const EMPTY_OBJECT = {};
export const PRINT_FAMILY_INVITE_THRESHOLD_LIMIT = 100;
export const LONG_RUNNING_ACTION_MINIMUM_ENTITY_COUNT = 6;

const getToastActionLabel = ({ action, t }) => {
  switch (action) {
    case "ADD":
      return t("toastMsgs:added");
    case "UPDATE":
      return t("toastMsgs:updated");
    case "DELETE":
      return t("toastMsgs:removed_from_label", {
        label: t("common:organization"),
      });
    case "ARCHIVE":
      return t("toastMsgs:archived");
    case "RESTORED":
      return _.toLower(t("common:restored"));
    case "ENROL":
      return t("toastMsgs:enrolled");
    case "UNENROL":
      return t("toastMsgs:unenrolled");
    case "BLOCKED":
      return t("toastMsgs:blocked");
    case "UNBLOCKED":
      return t("toastMsgs:unblocked");
  }
};

const _getCurriculumData = ({ organizationCurriculumPrograms }) => {
  let curriculumData = {};
  const isAYTv2Enabled = getIsAcademicYearTransitionV2Enabled();
  {
    _.map(organizationCurriculumPrograms, curriculumProgram => {
      const {
        acronym,
        label,
        id,
        academicYearTransitionConfigurationState,
      } = curriculumProgram;

      if (isAYTv2Enabled) {
        const completeConfigurationStatus =
          _.get(
            _.find(academicYearTransitionConfigurationState, {
              state: "COMPLETE_SETUP_CONFIGURATION",
            }),
            "isComplete"
          ) ?? false;

        curriculumData = {
          ...curriculumData,
          [id]: {
            acronym,
            label,
            yearGroupConfigurationStatus: completeConfigurationStatus,
            termsConfigurationStatus: completeConfigurationStatus,
            classConfigurationStatus: completeConfigurationStatus,
            staffConfigurationStatus: completeConfigurationStatus,
            studentsConfigurationStatus: completeConfigurationStatus,
          },
        };
      } else {
        const classConfigurationStatus =
          _.get(
            _.find(academicYearTransitionConfigurationState, {
              state: "CLASS_CONFIGURATION",
            }),
            "isComplete"
          ) ?? false;

        const staffConfigurationStatus =
          _.get(
            _.find(academicYearTransitionConfigurationState, {
              state: "STAFF_CONFIGURATION",
            }),
            "isComplete"
          ) ?? false;

        const studentsConfigurationStatus =
          _.get(
            _.find(academicYearTransitionConfigurationState, {
              state: "STUDENT_CONFIGURATION",
            }),
            "isComplete"
          ) ?? false;

        curriculumData = {
          ...curriculumData,
          [id]: {
            acronym,
            label,
            classConfigurationStatus,
            staffConfigurationStatus,
            studentsConfigurationStatus,
          },
        };
      }
    });
  }
  return curriculumData;
};

const getCurriculumData = _.memoize(_getCurriculumData, params =>
  JSON.stringify(params)
);

const getQueryVariables = data => {
  return data;
};
const getQueryVariablesMemoized = _.memoize(getQueryVariables, params =>
  JSON.stringify(params)
);

const getSelectedAcademicYearId = state => {
  const isAcademicYearTransitionMode = _.get(
    state,
    "academicYearTransition.isUsedForAcademicYearTransition",
    false
  );

  if (isAcademicYearTransitionMode) {
    return _.get(state, "academicYearTransition.academicYear.id", null);
  } else {
    return getAcademicYearId(state);
  }
};

const getSelectedAcademicYearObject = state => {
  const isAcademicYearTransitionMode = _.get(
    state,
    "academicYearTransition.isUsedForAcademicYearTransition",
    false
  );

  if (isAcademicYearTransitionMode) {
    return _.get(state, "academicYearTransition.academicYear", {});
  } else {
    return getAcademicYearObject(state);
  }
};

const _getCourseFeedQueryFilters = ({
  organizationId,
  courseFilters,
  orderBy,
  orderByDirection,
  academicYearId,
  collaboratorFilters,
  curriculumProgramIds,
  includeFiltersWithoutSearch = false,
}) => {
  return {
    organizationId,
    filters: courseFilters,
    orderBy,
    orderByDirection,
    academicYearId,
    collaboratorFilters,
    curriculumProgramIds,
    ...(includeFiltersWithoutSearch && {
      filtersWithoutSearch: {
        ...courseFilters,
        searchText: "",
      },
    }),
  };
};

const getCourseFeedQueryFilters = _.memoize(
  _getCourseFeedQueryFilters,
  JSON.stringify
);

const getStudentQueryVariables = ({
  organizationId,
  selectedAcademicYearId,
  userCourseMapFilters,
  orderByDirection,
  orderBy,
  staffFilter,
  studentQueryFilters,
  includeFiltersWithoutSearch = false, // Set to true when refetching getStudentFeedQueryV2
}) => {
  let userYearGroupMapFilters = {};

  userYearGroupMapFilters = {
    academicYearIds: [selectedAcademicYearId],
  };

  return {
    organizationId,
    courseGradeFilters: {
      academicYearId: selectedAcademicYearId,
    },

    studentCurriculumProgramFilter: {
      academicYearId: selectedAcademicYearId,
    },

    userCourseMapFilters,
    userYearGroupMapFilters,
    yearGroupGradeFilters: {
      academicYearId: selectedAcademicYearId,
    },
    filters: studentQueryFilters,
    orderByDirection,
    orderBy,
    staffFilter,
    withStaffRelation: true,
    familyStatus: {
      academicYearIds: selectedAcademicYearId ? [selectedAcademicYearId] : [],
    },
    userArchivedFilters: { academicYearId: selectedAcademicYearId },
    academicYears: [selectedAcademicYearId],
    ...(includeFiltersWithoutSearch && {
      filtersWithoutSearch: {
        ...studentQueryFilters,
        searchText: "",
      },
    }),
  };
};

const downloadTemplate = async ({
  organizationId,
  immediateFutureAcademicYearId,
  curriculumProgramId,
  setToastMsg,
}) => {
  const userInfo = getUserInfo();

  const response = await getTemplateUrl({
    organizationId,
    type: "ACADEMIC_YEAR_TRANSITION_DATA",
    curriculumProgramId,
    academicYearId: immediateFutureAcademicYearId,
    filters: {
      configureAcademicYearTransition: false,
    },
  });

  if (_.get(response, "isSuccess", false)) {
    setToastMsg({
      type: "info",
      msg: "toastMsgs:send_email_with_download_link",
      hasHTMLTags: true,
      locale_params: [
        {
          key: "email",
          value: userInfo?.email,
          isPlainText: true,
        },
      ],
      position: "toast-bottom-left",
      timeOut: 6000,
    });
  } else {
    setToastMsg("toastMsgs:something_went_wrong");
  }
};

const isUserArchivalAcademicYearWiseEnabled = () => {
  return ACLStore.can(ENABLE_USER_ACADEMIC_YEAR_ARCHIVAL_STATUS);
};

const isFilterDataDownloadEnabled = () => {
  return ACLStore.can(ENABLE_FILTERED_DATA_DOWNLOAD);
};

const getRoutesToRenderOnlyChildren = () => {
  return ["integrations/:curriculumId", "integrations/catalog/:service"];
};

const getIsIntegrationsModuleEnabled = () => {
  // This will check if INTEGRATIONS node - parent of all the child sync routes should be visible or not
  // If RnP is ON - it will fetch from module settings
  // If RnP is OFF - it will decide based on skipForTeacher flag and child permissions
  return getAdminPortalNodeVisibilityStatusByUID({
    uid: "INTEGRATIONS",
  });
};

export const getVariablesForAccountSetupPermissions = ({
  curriculumPrograms,
}) => {
  const contexts = [[]];

  const programsCount = _.size(curriculumPrograms);

  for (let i = 0; i < programsCount; i++) {
    const { id } = curriculumPrograms[i];

    const context = [
      {
        entityId: id,
        entityType: "CURRICULUM_PROGRAM",
      },
    ];

    contexts.push(context);
  }

  return {
    filters: {
      module: "ACCOUNT_SETUP",
      appType: ["ADMIN_WEB"],
      contexts,
    },
  };
};

const canConfigureSchoolOverview = () => {
  return ACLStorage.getPermissionValue(CAN_CONFIGURE_SCHOOL_OVERVIEW);
};

const canConfigureLoginOptions = () => {
  return ACLStorage.getPermissionValueByContext(
    CAN_CONFIGURE_LOGIN_OPTIONS,
    []
  );
};

const checkSchoolRosterPermissionValue = ({
  curriculumProgramId,
  accountLevelPermission,
  programLevelPermission,
}) => {
  const isAdminNavigationRevampEnabled = getIsNewAdminUIEnabled();

  if (isAdminNavigationRevampEnabled && getIsAccountConfigurationEnabled()) {
    if (programLevelPermission && curriculumProgramId) {
      return ACLStorage.getPermissionValueByContext(programLevelPermission, [
        { entityId: curriculumProgramId, entityType: "CURRICULUM_PROGRAM" },
      ]);
    }

    if (accountLevelPermission) {
      const accountLevelVisibility = ACLStorage.getPermissionValueByContext(
        accountLevelPermission,
        []
      );

      if (accountLevelVisibility) {
        return accountLevelVisibility;
      }
    }
  }

  const accountLevelVisibility = ACLStorage.getPermissionValueByContext(
    accountLevelPermission,
    []
  );

  if (accountLevelVisibility) {
    return accountLevelVisibility;
  }

  if (curriculumProgramId) {
    return ACLStorage.getPermissionValueByContext(programLevelPermission, [
      { entityId: curriculumProgramId, entityType: "CURRICULUM_PROGRAM" },
    ]);
  }

  return false;
};

export const canManageClassRoster = ({
  curriculumProgramId,
  isAccountPortal = false,
} = {}) => {
  const isAdminNavigationRevampEnabled = getIsNewAdminUIEnabled();

  return checkSchoolRosterPermissionValue({
    curriculumProgramId:
      isAccountPortal && !isAdminNavigationRevampEnabled
        ? undefined
        : curriculumProgramId,
    accountLevelPermission: isAdminNavigationRevampEnabled
      ? MANAGE_CLASS_ROSTER_PERMISSION
      : MANAGE_SCHOOL_ROSTER_PERMISSION_FOR_ACCOUNT,
    programLevelPermission: isAdminNavigationRevampEnabled
      ? MANAGE_CLASS_ROSTER_PERMISSION
      : MANAGE_SCHOOL_ROSTER_PERMISSION,
  });
};

const canManageSchoolRoster = curriculumProgramId => {
  return checkSchoolRosterPermissionValue({
    curriculumProgramId,
    accountLevelPermission: MANAGE_SCHOOL_ROSTER_PERMISSION_FOR_ACCOUNT,
    programLevelPermission: MANAGE_SCHOOL_ROSTER_PERMISSION,
  });
};

const canResetStudentCodeAndFamilyInviteInSchoolRoster = curriculumProgramId => {
  return checkSchoolRosterPermissionValue({
    curriculumProgramId,
    accountLevelPermission: SCHOOL_ROSTER_RESET_FAMILY_INVITE_AND_STUDENT_CODE_PERMISSION_FOR_ACCOUNT,
    programLevelPermission: SCHOOL_ROSTER_RESET_FAMILY_INVITE_AND_STUDENT_CODE_PERMISSION,
  });
};

const canViewStudentCodeAndFamilyInviteInSchoolRoster = curriculumProgramId => {
  return checkSchoolRosterPermissionValue({
    curriculumProgramId,
    accountLevelPermission: SCHOOL_ROSTER_VIEW_FAMILY_INVITE_AND_STUDENT_CODE_PERMISSION_FOR_ACCOUNT,
    programLevelPermission: SCHOOL_ROSTER_VIEW_FAMILY_INVITE_AND_STUDENT_CODE_PERMISSION,
  });
};

const canManageSuperAdmins = () => {
  return ACLStorage.getPermissionValue(CAN_ADD_SUPER_ADMINS_FOR_ACCOUNT);
};

export const canImpersonateStaff = (isDevUser = false) => {
  return (
    ACLStorage.getPermissionValue(CAN_IMPERSONATE_STAFF_PERMISSION) || isDevUser
  );
};

export const canImpersonateStudent = (isDevUser = false) => {
  return (
    ACLStorage.getPermissionValue(CAN_IMPERSONATE_STUDENT_PERMISSION) ||
    isDevUser
  );
};

export const canImpersonateStudentInClassSettings = (isDevUser = false) => {
  return (
    ACLStorage.getPermissionValue(
      CAN_IMPERSONATE_STUDENT_CLASS_SETTINGS_PERMISSION
    ) || isDevUser
  );
};

export const canLogoutStaff = () => {
  return ACLStorage.getPermissionValue(CAN_LOGOUT_STAFF_PERMISSION);
};

export const getFilteredDividers = actions => {
  const filteredActions = actions.reduce((acc, action) => {
    if (action.isDivider) {
      if (acc.length > 0 && !acc[acc.length - 1].isDivider) {
        acc.push(action);
      }
    } else {
      acc.push(action);
    }
    return acc;
  }, []);

  if (filteredActions[filteredActions.length - 1]?.isDivider) {
    filteredActions.pop();
  }

  return filteredActions;
};

export {
  getToastActionLabel,
  getCurriculumData,
  getQueryVariablesMemoized,
  getSelectedAcademicYearId,
  getSelectedAcademicYearObject,
  getCourseFeedQueryFilters,
  getStudentQueryVariables,
  downloadTemplate,
  isUserArchivalAcademicYearWiseEnabled,
  isFilterDataDownloadEnabled,
  getRoutesToRenderOnlyChildren,
  getIsIntegrationsModuleEnabled,
  canConfigureSchoolOverview,
  canManageSchoolRoster,
  canResetStudentCodeAndFamilyInviteInSchoolRoster,
  canViewStudentCodeAndFamilyInviteInSchoolRoster,
  canManageSuperAdmins,
  canConfigureLoginOptions,
};

export const getHeaderCheckboxState = ({
  areAllItemsSelected,
  selectedOrExcludedItems,
  totalCount,
}) => {
  if (areAllItemsSelected) {
    const excludedCount = _.size(selectedOrExcludedItems);
    return {
      isChecked: excludedCount === 0,
      isIndeterminate: excludedCount > 0 && excludedCount < totalCount,
    };
  } else {
    const selectedCount = _.size(selectedOrExcludedItems);
    return {
      isChecked: totalCount > 0 && selectedCount === totalCount,
      isIndeterminate: selectedCount > 0 && selectedCount < totalCount,
    };
  }
};

/**
 * Updates the selection list when a user checks or unchecks an item.
 *
 * - If "allSelected" is true:
 *   - Everything is selected by default.
 *   - The list keeps track of manually unchecked items (exceptions).
 *   - Checking an item removes it from the list (re-selecting it).
 *   - Unchecking an item adds it to the list (deselecting it).
 *
 * - If "allSelected" is false:
 *   - Nothing is selected by default.
 *   - The list keeps track of manually checked items.
 *   - Checking an item adds it to the list (selecting it).
 *   - Unchecking an item removes it from the list (deselecting it).
 *
 * This makes selection behave naturally whether the user is selecting a few items
 * or using "Select All" with some exceptions.
 */
export const updateSelectionForBulkAction = ({
  areAllItemsSelected,
  selectedOrExcludedItems,
  itemId,
  isChecked,
}) => {
  if (areAllItemsSelected) {
    // Bulk mode: selectedOrExcludedItems = exclusions from "select all"
    return isChecked
      ? _.filter(selectedOrExcludedItems, id => id !== itemId) // remove from exclusions
      : _.concat(selectedOrExcludedItems, itemId); // add to exclusions
  } else {
    // Normal mode: selectedOrExcludedItems = explicitly selected items
    return isChecked
      ? _.uniq(_.concat(selectedOrExcludedItems, itemId))
      : _.filter(selectedOrExcludedItems, id => id !== itemId);
  }
};

/**
 * Filters a list of entities based on select-all logic and a list of included or excluded IDs.
 *
 * @param {Object[]} allEntities - The full list of entities.
 * @param {string[]} includedOrExcludedIds - IDs to include (if selectAllMode is off) or exclude (if selectAllMode is on).
 * @param {boolean} isSelectAllMode - Whether "Select All" mode is active.
 * @param {string} [entityIdKey='id'] - The key to use as the entity's unique identifier.
 * @returns {Object[]} - Filtered list of entities.
 */
export function filterEntitiesBySelection({
  allEntities,
  includedOrExcludedIds,
  isSelectAllMode,
  entityIdKey = "id",
}) {
  const isListOfIds = _.isString(_.first(allEntities));

  return _.filter(allEntities, entity => {
    const id = isListOfIds ? entity : _.get(entity, entityIdKey);
    const isIncluded = _.includes(includedOrExcludedIds, id);
    return isSelectAllMode ? !isIncluded : isIncluded;
  });
}

export const getNormalizedLocale = locale => {
  const ENGLISH_VARIANTS = ["en", "en-US", "en-GB"];
  if (!locale || typeof locale !== "string") return "en";

  return _.includes(ENGLISH_VARIANTS, locale) ? "en" : locale;
};

export const getFileNameForBulkDownload = ({
  type,
  orgName,
  curriculumAcronym,
  t,
}) => {
  const date = moment().format("D MMM YYYY");
  let title = "";

  switch (type) {
    case "print_family_invite": {
      title = t("common:family_invite_plural");
      break;
    }
    case "print_student_sign_in_codes": {
      title = t("common:student_sign_in_code_plural");
      break;
    }
    case "class_invite_code": {
      title = t("schoolSetup:class_invite_code");
      break;
    }
    default:
      return "";
  }
  const fileName = _.isEmpty(curriculumAcronym)
    ? `${title} - ${orgName} (${date})`
    : `${curriculumAcronym} ${title} - ${orgName} (${date})`;

  return fileName;
};

export const getCurriculumProgramOptions = ({ t, curriculumPrograms }) => {
  return [
    {
      label: t("common:all"),
      key: "ALL_CURRICULUM_PROGRAMS",
      acronym: t("common:all"),
    },
    ..._.map(curriculumPrograms, curriculum => ({
      label: getCurriculumProgramLabel({
        curriculumProgram: curriculum,
        shouldIncludeAcronym: true,
      }),
      key: _.get(curriculum, "id"),
      type: _.get(curriculum, "type"),
      acronym: _.get(curriculum, "acronym"),
    })),
  ];
};

export const getLevelsQueryVariables = ({
  curriculumProgramId,
  curriculumType,
  archivalState = "ACTIVE",
}) => {
  return {
    curriculumProgramId,
    curriculumType,
    levelsFilters: {
      archivalState,
      types: ["SUBJECT_LEVEL", "LEVELS"],
    },
  };
};
